import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {
    CrActivitySummaryResponse,
    HMPDetailsReport,
    HMPDetailsReportResponse,
    IActivityCountRespose,
    IActivityMediumOverviewResponse,
    ICRMediationClosedActvityOverviewResponse,
    IDRPReportSchema,
    IDRPReportSchemaResponse,
    IDemographicsLocatoionResponse,
    IDemographicsResponse,
    IDispositionDisputeType,
    IDynamicReportSchema,
    IEntityNameCountResponse,
    IEntityTypeCountResponse,
    IFTFReportSchema,
    IFTFReportSchemaResponse,
    IHMPSummarySchema,
    IHmpSummary,
    IMBFReportSchema,
    IMBFReportSchemaResponse,
    IOutreachResponse,
    IRemapCaseSchema,
    IRemapCaseSchemaResponse,
    IRemapInfoSessionSchema,
    IRemapInfoSessionSchemaResponse,
    IRemapParticipantSchema,
    IRemapParticipantSchemaResponse,
    IServiceLocationCountResponse,
    ITCRReportSchema,
    ITcrReportSchemaResponse,
    IUserCountResponse,
    IUserDetailsResponse,
    LocationNameCountResponse,
    URL_REPORTS_MOPC_ACTIVITY_COUNT,
    URL_REPORTS_MOPC_ACTIVITY_MEDIUM_OVERVIEW,
    URL_REPORTS_MOPC_LOCATION_NAME_COUNT,
    URL_REPORTS_MOPC_SERVICE_LOCATION_COUNT,
} from '@evencel/models';
import {Observable, take} from 'rxjs';

export const REPORT_URL = 'reports';

@Injectable({providedIn: 'root'})
export class ReportService {
    httpClient = inject(HttpClient);

    hmp(data: HMPDetailsReport): Observable<HMPDetailsReportResponse[]> {
        return this.httpClient.post<HMPDetailsReportResponse[]>(`${REPORT_URL}/hmp`, data);
    }

    hmpSummary(data: IHMPSummarySchema, center: string): Observable<IHmpSummary> {
        const headers = new HttpHeaders({center: center});
        return this.httpClient.post<IHmpSummary>(`${REPORT_URL}/hmp-summary`, data, {headers});
    }

    ftp(data: IFTFReportSchema, center: string): Observable<IFTFReportSchemaResponse> {
        const headers = new HttpHeaders({
            center: center,
        });
        return this.httpClient.post<IFTFReportSchemaResponse>(`${REPORT_URL}/ftf-report`, data, {headers});
    }

    mbf(data: IMBFReportSchema, center: string): Observable<IMBFReportSchemaResponse> {
        const headers = new HttpHeaders({
            center: center,
        });
        return this.httpClient.post<IMBFReportSchemaResponse>(`${REPORT_URL}/mbf`, data, {headers});
    }

    drp(data: IDRPReportSchema, center: string): Observable<IDRPReportSchemaResponse[]> {
        const headers = new HttpHeaders({
            center: center,
        });
        return this.httpClient.post<IDRPReportSchemaResponse[]>(`${REPORT_URL}/drp`, data, {headers});
    }

    tcr(data: ITCRReportSchema, center: string): Observable<ITcrReportSchemaResponse> {
        const headers = new HttpHeaders({
            center: center,
        });
        return this.httpClient.post<ITcrReportSchemaResponse>(`${REPORT_URL}/tcr`, data, {headers});
    }

    remapParticipant(data: IRemapParticipantSchema, center: string): Observable<IRemapParticipantSchemaResponse[]> {
        const headers = new HttpHeaders({center: center});
        return this.httpClient.post<IRemapParticipantSchemaResponse[]>(`${REPORT_URL}/remap-participant-data`, data, {
            headers,
        });
    }

    remapCase(data: IRemapCaseSchema, center: string): Observable<IRemapCaseSchemaResponse[]> {
        const headers = new HttpHeaders({
            center: center,
        });
        return this.httpClient.post<IRemapCaseSchemaResponse[]>(`${REPORT_URL}/remap-case`, data, {headers});
    }

    remapInfoSession(data: IRemapInfoSessionSchema, center: string): Observable<IRemapInfoSessionSchemaResponse[]> {
        const headers = new HttpHeaders({
            center: center,
        });
        return this.httpClient.post<IRemapInfoSessionSchemaResponse[]>(`${REPORT_URL}/remap-info-session`, data, {
            headers,
        });
    }

    demographics(data: IDynamicReportSchema) {
        return this.httpClient.post<IDemographicsResponse>(`${REPORT_URL}/mopc-demographics`, data);
    }

    dispositionDispute(data: IDynamicReportSchema) {
        return this.httpClient
            .post<IDispositionDisputeType[]>(`${REPORT_URL}/mopc-disposition-dispute`, data)
            .pipe(take(1));
    }

    dispositionEntity(data: IDynamicReportSchema) {
        return this.httpClient
            .post<IDispositionDisputeType[]>(`${REPORT_URL}/mopc-disposition-entity`, data)
            .pipe(take(1));
    }

    outreach(data: IDynamicReportSchema) {
        return this.httpClient.post<IOutreachResponse>(`${REPORT_URL}/mopc-outreach`, data).pipe(take(1));
    }

    demographicsLocation(data: IDynamicReportSchema) {
        return this.httpClient.post<IDemographicsLocatoionResponse[]>(
            `${REPORT_URL}/mopc-demographics-locations`,
            data
        );
    }

    entityTypes(data: IDynamicReportSchema) {
        return this.httpClient.post<IEntityTypeCountResponse>(`${REPORT_URL}/mopc-entity-types`, data);
    }

    entityNames(data: IDynamicReportSchema) {
        return this.httpClient.post<IEntityNameCountResponse>(`${REPORT_URL}/mopc-entity-names`, data);
    }

    userCounts(data: IDynamicReportSchema): Observable<IUserCountResponse> {
        return this.httpClient.post<IUserCountResponse>(`${REPORT_URL}/mopc-user-counts`, data);
    }

    userDetailsCounts(data: IDynamicReportSchema) {
        return this.httpClient.post<IUserDetailsResponse>(`${REPORT_URL}/mopc-user-details`, data);
    }

    crActivitySummaryOverview(data: IDynamicReportSchema) {
        return this.httpClient.post<CrActivitySummaryResponse>(`${REPORT_URL}/mopc-cr-activity-summary`, data);
    }

    locationNameCount(data: IDynamicReportSchema) {
        const url = `${REPORT_URL}/${URL_REPORTS_MOPC_LOCATION_NAME_COUNT}`;
        return this.httpClient.post<LocationNameCountResponse>(url, data);
    }

    serviceLocationCount(data: IDynamicReportSchema) {
        const url = `${REPORT_URL}/${URL_REPORTS_MOPC_SERVICE_LOCATION_COUNT}`;
        return this.httpClient.post<IServiceLocationCountResponse>(url, data);
    }

    activityOrverview(data: IDynamicReportSchema) {
        const url = `${REPORT_URL}/${URL_REPORTS_MOPC_ACTIVITY_MEDIUM_OVERVIEW}`;
        return this.httpClient.post<IActivityMediumOverviewResponse>(url, data);
    }

    activtyCount(data: IDynamicReportSchema) {
        const url = `${REPORT_URL}/${URL_REPORTS_MOPC_ACTIVITY_COUNT}`;
        return this.httpClient.post<IActivityCountRespose>(url, data);
    }

    getMOPCCRMediationActivityReports(data: IDynamicReportSchema) {
        return this.httpClient.post<ICRMediationClosedActvityOverviewResponse>(
            `${REPORT_URL}/mopc-cr-closed-activities`,
            data
        );
    }
}
